import React, { useState, useMemo, useEffect } from "react";
import { Form, FormGroup, Label, Input, Button, Card, CardBody, CardTitle, Row, Col } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomSelect from "components/Common/custom-select";
import TableContainer from "components/Common/TableContainer";
import { FaDownload, FaEye, FaPlusCircle } from "react-icons/fa";
import { FiDownload, FiEye, FiPlusCircle } from "react-icons/fi";
import nv1 from "../../../assets/images/brands/b-nav-1.png";
import { useDispatch, useSelector } from "react-redux";
import {
  createEmployee,
  fetchEmployeeTabWiseInfo,
} from "store/actions";
import { handleDownload } from "common/fileDownload";
import CustomDatePicker from "common/CustomDatePicker";

// Dropdown options for status
const statusOptions = [
  { value: "1", label: "Assigned" },
  { value: "2", label: "Returned" },
  { value: "3", label: "Lost" },
];

const alphaNumSpecial100Regex = /^[A-Za-z0-9\s.,\-/#()]+$/;
const alphaNumSpecial20Regex = /^[A-Za-z0-9\s.,\-/#()]+$/;
// select custome style
const SelectcustomStyles = {
  control: (base) => ({
    ...base,
    maxheight: 33.1,
    minHeight: 33.1,
    // overflow: "scroll",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: "0", // Match the padding for both dropdown and clear indicator
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "2px 8px", // Ensure consistent padding for dropdown indicator
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "2px 8px", // Adjust padding for clear indicator to match dropdown
  }),
};

const EmployeeEquipment = ({ empID, authDetails, isActive, dropdownData }) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const { approve_by } = dropdownData;
  const reportOptions = (approve_by || []).map((report) => ({
    value: report.report_id,
    label: report.report_name
  }));
  const {
    employeeEquipmentInfo = [],
    success,
    loading,
    error,
  } = useSelector((state) => ({
    employeeEquipmentInfo: empID == 0 ? [] : state.EmployeeDetailsReducer?.employeeTabData?.equipment || [],

    loading: state.employeeReducer?.loading,
    error: state.employeeReducer?.error,
    success: state.EmployeeDetailsReducer?.success
  }));

  const formik = useFormik({
    initialValues: {
      description: "",
      equipmentNo: "",
      issueDate: null,
      make: "",
      value: "",
      status: "",
      returnDate: null,
      approvedBy: "",
      file: null,
    },
     validationSchema: Yup.object({
      description: Yup.string()
    .required("Description is required")
    .max(100, "Description must not exceed 100 characters")
    .matches(
      alphaNumSpecial100Regex,
      "Only alphabets, numerics and special characters allowed"
    ),
 
  equipmentNo: Yup.string()
    .required("Equipment Number is required")
    .max(20, "Equipment Number must not exceed 20 characters")
    .matches(
      alphaNumSpecial20Regex,
      "Only alphabets, numerics and special characters allowed"
    ),
      issueDate: Yup.date()
        .nullable()
        .required("Issue Date is required"),
      make: Yup.string()
    .required("Make Year is required")
    .matches(/^\d{4}$/, "Make Year must be 4 digits"),
 
  value: Yup.string()
    .required("Value is required")
    .matches(/^\d{1,7}$/, "Value must be numeric and max 7 digits"),
      status: Yup.string()
        .required("Status is required"),
      returnDate: Yup.date()
        .nullable(), // Optional, no message needed unless required
      approvedBy: Yup.string()
        .required("Approved By is required"),
      file: Yup.mixed()
        .test("fileRequired", "File is required", (value) => value !== null)
        .test("fileSize", "File size is too large (max 2MB)", (value) =>
          value ? value.size <= 2 * 1024 * 1024 : true
        )
        .test(
          'fileType',
          'Only JPG, JPEG, PNG, and PDF formats are allowed',
          (value) =>
            value &&
            ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'].includes(value.type)
        ),
    }),
    onSubmit: (values) => {
      // console.log("Submitted Equipment:", values);

      const payload = {
        "eqp": [
          {
            emp_id: empID,
            equipment_name: values.description,
            equipment_no: values.equipmentNo,
            issue_dt: values.issueDate,
            return_dt: values.returnDate,
            make: values.make,
            value: values.value,
            eq_statusid: values.status,
            approved_by: values.approvedBy
          }
        ]
      }
      const formData = new FormData();
      formData.append("lst_employee_Others", JSON.stringify(payload));
      formData.append("create_by", authDetails?.user_id);
      formData.append("equipment_file", values.file);
      dispatch(createEmployee(formData, 'equipment'));
      formik.resetForm()
      setSelectedFile(null)
    },
  });

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        Cell: ({ row }) => {
          const fileUrl = row.original?.file_path;
          const filePath = `${process.env.REACT_APP_IMAGE_BASE_URL}${fileUrl}`;
          const fileName = fileUrl.split("/").pop();
          return filePath ? (
            <div className="flex gap-2">
              <a href={filePath} target="_blank" rel="noopener noreferrer">
                <FiEye className="text-blue-500 cursor-pointer me-2" title="View" />
              </a>
              <a href={filePath} download={fileName} onClick={(e) => {
                e.preventDefault(); // Prevent default behavior
                handleDownload(filePath, fileName);
              }}>
                <FiDownload className="text-green-500 cursor-pointer" title="Download" />
              </a>
            </div>
          ) : (
            ""
          );
        },
      },
      { Header: "S.No", Cell: ({ row }) => employeeEquipmentInfo.length - row.index, className: "serial-no", applyHeaderClass: true },
      { Header: "Description", accessor: "equipment_name" }, // Mapping `equipment_name`
      { Header: "Equipment No", accessor: "equipment_no" }, // Mapping `equipment_no`
      { Header: "Issue Date", accessor: "issue_dt" }, // Mapping `issue_dt`
      { Header: "Return Date", accessor: "return_dt" }, // Mapping `return_dt`
      { Header: "Make", accessor: "make" }, // Mapping `make`
      { Header: "Value", accessor: "value" }, // Mapping `value`
      { Header: "Status", accessor: "eq_status" }, // Mapping `eq_status`
      { Header: "Approved By", accessor: "approver" }, // Mapping `approver`
      
    ],
    [employeeEquipmentInfo]
  );

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    setSelectedFile(file);
    formik.setFieldValue("file", file);
  };

  useEffect(() => {
    if (isActive) {
      dispatch(fetchEmployeeTabWiseInfo(empID, 'equipment'));
    }
  }, [dispatch, isActive]);

  const [fileKey, setFileKey] = useState(Date.now());
  useEffect(() => {
      if (success) {
      formik.setFieldValue("file", null);

      // regenerate key — browser clears file input
      setFileKey(Date.now());
      }
  }, [success]);

  return (
    <>
      <Card>
        <div className="card-main-title">
          <h6 className="title">
            <img src={nv1} className="img-fluid w-auto h-auto" alt="img" />
            Employee Equipment
          </h6>
        </div>
        <CardBody>

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Description <span className="text-danger">*</span></Label>
                  <Input type="text" {...formik.getFieldProps("description")} maxLength={100} />
                  {formik.touched.description && formik.errors.description && (
                    <div className="text-danger">{formik.errors.description}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Equipment No <span className="text-danger">*</span></Label>
                  <Input type="text" {...formik.getFieldProps("equipmentNo")} maxLength={20} />
                  {formik.touched.equipmentNo && formik.errors.equipmentNo && (
                    <div className="text-danger">{formik.errors.equipmentNo}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Issue Date <span className="text-danger">*</span></Label>
                  {/* <Input
                    type="date"
                    id="issueDate"
                    name="issueDate"
                    value={formik.values.issueDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.issueDate && !!formik.errors.issueDate}
                  /> */}
                  <CustomDatePicker
                    value={formik.values.issueDate}
                    onChange={(date) => formik.setFieldValue("issueDate", date)}
                    onBlur={() => formik.setFieldTouched("issueDate", true)}
                    disableFuture={true}
                  />
                  {formik.touched.issueDate && formik.errors.issueDate && (
                    <div className="text-danger">{formik.errors.issueDate}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label>Make Year <span className="text-danger">*</span></Label>
                  <Input type="text" {...formik.getFieldProps("make")} maxLength={4}/>
                  {formik.touched.make && formik.errors.make && (
                    <div className="text-danger">{formik.errors.make}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label>Value (₹) <span className="text-danger">*</span></Label>
                  <Input type="text" {...formik.getFieldProps("value")} maxLength={7} />
                  {formik.touched.value && formik.errors.value && (
                    <div className="text-danger">{formik.errors.value}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Status <span className="text-danger">*</span></Label>
                  <CustomSelect
                    styles={SelectcustomStyles}
                    options={statusOptions}
                    name="status"
                    value={statusOptions.find((s) => s.value === formik.values.status) || null}
                    onChange={(selectedOption) => formik.setFieldValue("status", selectedOption?.value || "")}
                  />
                  {formik.touched.status && formik.errors.status && (
                    <div className="text-danger">{formik.errors.status}</div>
                  )}
                </FormGroup>
              </Col>
          {formik.values.status === "2" && (
              <Col md={4}>
                <FormGroup>
                  <Label>Return Date</Label>
                  {/* <Input
                    type="date"
                    id="returnDate"
                    name="returnDate"
                    value={formik.values.returnDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.returnDate && !!formik.errors.returnDate}
                  /> */}
                  <CustomDatePicker
                    value={formik.values.returnDate}
                    onChange={(date) => formik.setFieldValue("returnDate", date)}
                    onBlur={() => formik.setFieldTouched("returnDate", true)}
                    disableFuture={true}
                  />
                  {formik.touched.returnDate && formik.errors.returnDate && (
                    <div className="text-danger">{formik.errors.returnDate}</div>
                  )}
                </FormGroup>
              </Col>

              )}

              <Col md={4}>
                <FormGroup>
                  <Label>Approved By <span className="text-danger">*</span></Label>
                  <CustomSelect
                    options={reportOptions}
                    name="approvedBy"
                    value={reportOptions.find((option) => option.value === formik.values.approvedBy) || null}
                    onChange={(selectedOption) => formik.setFieldValue("approvedBy", selectedOption ? selectedOption.value : "")}
                    onBlur={() => formik.setFieldTouched("approvedBy", true)} isClearable
                  />
                  {formik.touched.approvedBy && formik.errors.approvedBy && (
                    <div className="text-danger">{formik.errors.approvedBy}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Upload File <span className="text-danger">*</span></Label>
                  <Input key={fileKey} type="file" onChange={handleFileChange} />
                  {/* {selectedFile && <p className="text-success">Selected File: {selectedFile.name}</p>} */}
                  {formik.touched.file && formik.errors.file && (
                    <div className="text-danger">{formik.errors.file}</div>
                  )}
                </FormGroup>
              </Col>

              <Col className="d-flex justify-content-end align-items-center">
                <Button type="submit" className="d-flex align-items-center btn button-add">
                  <i className="fas fa-plus me-2" /> Add
                </Button>
              </Col>
            </Row>
          </Form>

        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Row className="mt-3">
            <Col md={12}>
              <TableContainer
                columns={columns}
                data={employeeEquipmentInfo.length > 0 ? employeeEquipmentInfo : []}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default EmployeeEquipment;
